import {
  Actions,
  Autosuggest,
  Box,
  Button,
  ButtonLink,
  Checkbox,
  ContentBlock,
  Dropdown,
  filterSuggestions,
  Heading,
  Stack,
  Text,
  Textarea,
  TextField,
} from 'braid-design-system';
import React from 'react';

import {
  EXPIRY_MONTHS,
  FIELD_LIMITS,
  SERVICE_TYPES,
  type ExpiryMonths,
} from '#shared/constants';
import type { ListingSubmission } from '#shared/types';
import { validateSubmission } from '#shared/validateSubmission';
import { presignUpload, submitListing } from '#src/api/communityDirectory';
import { PageWrapper } from '#src/components/PageWrapper/PageWrapper';
import { resizeImageFile, uploadToPresignedPost } from '#src/utils/imageResize';

const HONEYPOT_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '-9999px',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
};

const SERVICE_TYPE_SUGGESTIONS = SERVICE_TYPES.map((text) => ({ text }));
const filterServiceTypes = filterSuggestions(SERVICE_TYPE_SUGGESTIONS);

export const SubmitListing = () => {
  const formRenderedAt = React.useRef(Date.now());
  const [name, setName] = React.useState('');
  const [serviceType, setServiceType] = React.useState({ text: '' });
  const [phone, setPhone] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [website, setWebsite] = React.useState('');
  const [blurb, setBlurb] = React.useState('');
  const [honeypot, setHoneypot] = React.useState('');
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [expiryMonths, setExpiryMonths] = React.useState<ExpiryMonths>(12);
  const [acceptedTerms, setAcceptedTerms] = React.useState(false);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submission: ListingSubmission = {
      name: name.trim() || undefined,
      serviceType: serviceType.text.trim() || undefined,
      phone: phone.trim() || undefined,
      email: email.trim() || undefined,
      website: website.trim() || undefined,
      blurb: blurb.trim() || undefined,
      expiryMonths,
      acceptedTerms,
      websiteUrl: honeypot || undefined,
      formRenderedAt: formRenderedAt.current,
    };

    const validation = validateSubmission(submission, { checkTiming: false });
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    setErrors({});
    setSubmitting(true);

    try {
      let imageKey: string | undefined;

      if (imageFile) {
        const { blob, contentType } = await resizeImageFile(imageFile);
        const presigned = await presignUpload(contentType);
        await uploadToPresignedPost(
          presigned.url,
          presigned.fields,
          blob,
          contentType,
        );
        imageKey = presigned.key;
      }

      await submitListing({
        ...validation.sanitized,
        imageKey,
        acceptedTerms,
        formRenderedAt: formRenderedAt.current,
      });
      setSuccess(true);
    } catch (err) {
      if (err && typeof err === 'object' && 'errors' in err) {
        setErrors((err as { errors: Record<string, string> }).errors);
      } else {
        setErrors({ _form: 'Something went wrong. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <PageWrapper>
        <ContentBlock>
          <Stack space="large">
            <Heading level="1">Thank you!</Heading>
            <Text>
              Your listing has been submitted and will appear once reviewed by
              the PFA committee.
            </Text>
            <Actions>
              <ButtonLink href="/community-directory">
                Back to Community Directory
              </ButtonLink>
            </Actions>
          </Stack>
        </ContentBlock>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ContentBlock>
        <Stack space="xlarge">
          <Stack space="medium">
            <Heading level="1">Submit a listing</Heading>
            <Text>
              Share your business, skill or service with the Amsleigh Park
              community. Please include a name or business, a service type, and
              at least one way to contact you. Listings are reviewed before
              being published to ensure no spam or inappropriate content.
            </Text>
          </Stack>

          <Box component="form" onSubmit={handleSubmit}>
            <Stack space="xlarge">
              {errors._form ? (
                <Text tone="critical">{errors._form}</Text>
              ) : null}
              <Stack space="medium">
                <TextField
                  id="submit-name"
                  label="Name or business"
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                  message={errors.name}
                  tone={errors.name ? 'critical' : undefined}
                />

                <Autosuggest
                  id="submit-service-type"
                  label="Service type"
                  value={serviceType}
                  onChange={setServiceType}
                  onClear={() => setServiceType({ text: '' })}
                  suggestions={filterServiceTypes(serviceType)}
                  message={
                    errors.serviceType ??
                    'Choose a type or enter your own (e.g. Tutoring, Plumbing)'
                  }
                  tone={errors.serviceType ? 'critical' : undefined}
                />

                <Textarea
                  id="submit-blurb"
                  label="Short description"
                  value={blurb}
                  onChange={(e) => setBlurb(e.currentTarget.value)}
                  characterLimit={FIELD_LIMITS.blurb}
                  message={
                    errors.blurb ??
                    `A brief summary of what you offer (max ${FIELD_LIMITS.blurb} characters). Will be displayed on your listing card.`
                  }
                  tone={errors.blurb ? 'critical' : undefined}
                />
              </Stack>
              <Stack space="medium">
                <Heading level="3">How to contact you</Heading>
                {errors.contact ? (
                  <Text tone="critical">{errors.contact}</Text>
                ) : null}
                <TextField
                  id="submit-phone"
                  label="Phone"
                  value={phone}
                  onChange={(e) => setPhone(e.currentTarget.value)}
                  message={errors.phone}
                  tone={errors.phone ? 'critical' : undefined}
                  inputMode="tel"
                />

                <TextField
                  id="submit-email"
                  label="Email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  message={errors.email}
                  tone={errors.email ? 'critical' : undefined}
                />

                <TextField
                  id="submit-website"
                  label="Website"
                  value={website}
                  onChange={(e) => setWebsite(e.currentTarget.value)}
                  message={errors.website}
                  tone={errors.website ? 'critical' : undefined}
                />
              </Stack>
              <Stack space="small">
                <Text weight="strong">Photo, logo or image (optional)</Text>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) =>
                    setImageFile(e.currentTarget.files?.[0] ?? null)
                  }
                />
                <Text tone="secondary">JPEG, PNG or WebP, up to 5 MB</Text>
              </Stack>

              <Dropdown
                id="submit-expiry-months"
                label="Listing duration"
                value={String(expiryMonths)}
                onChange={(e) =>
                  setExpiryMonths(Number(e.currentTarget.value) as ExpiryMonths)
                }
                message={
                  errors.expiryMonths ??
                  'Your listing will be removed automatically after this period'
                }
                tone={errors.expiryMonths ? 'critical' : undefined}
              >
                {EXPIRY_MONTHS.map((months) => (
                  <option key={months} value={months}>
                    {months} months
                  </option>
                ))}
              </Dropdown>

              <Checkbox
                id="submit-accepted-terms"
                label="I acknowledge that Amsleigh Park Primary School and the Amsleigh Park PFA take no responsibility for the services listed, and place no expectations on those services. Listings are a free community courtesy and do not imply endorsement or any obligation to provide discounted or free work."
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.currentTarget.checked)}
                message={errors.acceptedTerms}
                tone={errors.acceptedTerms ? 'critical' : undefined}
              />

              <Box style={HONEYPOT_STYLE} aria-hidden="true">
                <label htmlFor="submit-honeypot">Website URL</label>
                <input
                  id="submit-honeypot"
                  type="text"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.currentTarget.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </Box>

              <Stack space="small">
                <Actions>
                  <Box
                    style={{
                      opacity: acceptedTerms ? 1 : 0.5,
                      pointerEvents: acceptedTerms ? undefined : 'none',
                    }}
                  >
                    <Button
                      type="submit"
                      loading={submitting}
                      tone="formAccent"
                    >
                      Submit for review
                    </Button>
                  </Box>
                  <ButtonLink href="/community-directory" variant="transparent">
                    Cancel
                  </ButtonLink>
                </Actions>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </ContentBlock>
    </PageWrapper>
  );
};
